const ENTRY_SELECTOR = '[data-landing-entry]';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const ENTRY_EASING = 'cubic-bezier(0.25, 0.1, 0.25, 1)';

const TRANSITIONS = {
  actions: {
    delay: 400,
    duration: 800,
    keyframes: [
      { opacity: 0, transform: 'translateY(20%)', filter: 'blur(10px)' },
      { opacity: 1, transform: 'none', filter: 'none' },
    ],
  },
  support: {
    delay: 240,
    duration: 800,
    keyframes: [
      { opacity: 0, transform: 'translateY(20%)', filter: 'blur(10px)' },
      { opacity: 1, transform: 'none', filter: 'none' },
    ],
  },
  timer: {
    delay: 720,
    duration: 650,
    keyframes: [
      { opacity: 0, transform: 'scaleX(0.94)', filter: 'blur(4px)' },
      { opacity: 1, transform: 'none', filter: 'none' },
    ],
  },
  visual: {
    delay: 560,
    duration: 800,
    keyframes: [
      { opacity: 0, transform: 'translate3d(0, 16px, 0) scale(0.985)' },
      { opacity: 1, transform: 'none' },
    ],
  },
  'visual-content': {
    delay: 640,
    duration: 800,
    keyframes: [
      { opacity: 0, filter: 'blur(10px)' },
      { opacity: 1, filter: 'none' },
    ],
  },
  word: {
    delay: 0,
    duration: 800,
    keyframes: [
      { opacity: 0, transform: 'translateY(20%)', filter: 'blur(10px)' },
      { opacity: 1, transform: 'none', filter: 'none' },
    ],
  },
};

const DESKTOP_TRANSITIONS = {
  actions: {
    delay: 400,
    duration: 680,
    keyframes: [
      { opacity: 0, transform: 'translateY(12px)', filter: 'blur(6px)' },
      { opacity: 1, transform: 'none', filter: 'none' },
    ],
  },
  support: {
    delay: 220,
    duration: 680,
    keyframes: [
      { opacity: 0, transform: 'translateY(12px)', filter: 'blur(6px)' },
      { opacity: 1, transform: 'none', filter: 'none' },
    ],
  },
  timer: {
    delay: 660,
    duration: 540,
    keyframes: [
      { opacity: 0, transform: 'scaleX(0.96)', filter: 'blur(3px)' },
      { opacity: 1, transform: 'none', filter: 'none' },
    ],
  },
  visual: {
    delay: 500,
    duration: 720,
    keyframes: [
      { opacity: 0, transform: 'translate3d(0, 18px, 0) scale(0.988)' },
      { opacity: 1, transform: 'none' },
    ],
  },
  'visual-content': {
    delay: 580,
    duration: 620,
    keyframes: [
      { opacity: 0, transform: 'translateY(6px)', filter: 'blur(5px)' },
      { opacity: 1, transform: 'none', filter: 'none' },
    ],
  },
  word: {
    delay: 0,
    duration: 680,
    keyframes: [
      { opacity: 0, transform: 'translateY(18%)', filter: 'blur(7px)' },
      { opacity: 1, transform: 'none', filter: 'none' },
    ],
  },
};

// Storyboard: mobile/tablet words start at 0ms + 40ms each, then support 240,
// actions 400, visuals 560 + 80, visual content 640 + 80, and timers 720ms;
// all run 800ms except timers at 650ms. Desktop starts those phases at 0, 220,
// 400, 500, 580, and 660ms with 24/110ms staggers and 680/680/680/720/620/540ms durations.
const root = document.documentElement;
const landing = document.querySelector('[data-landing-entry-root]');
const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
const activeAnimations = new Map();
let entryComplete = false;
let entryWatchdog = 0;

const removeMotionPreferenceListener = () => {
  if (typeof reducedMotion.removeEventListener === 'function') {
    reducedMotion.removeEventListener('change', handleMotionPreferenceChange);
    return;
  }

  reducedMotion.removeListener?.(handleMotionPreferenceChange);
};

const removeInterruptionListeners = () => {
  landing?.removeEventListener('focusin', handleEntryFocus);
  window.removeEventListener('pagehide', handleEntryInterruption);
  document.removeEventListener('visibilitychange', handleEntryVisibility);
  removeMotionPreferenceListener();
};

const cleanupAnimation = (element, animation) => {
  if (activeAnimations.get(element) !== animation) {
    return;
  }

  activeAnimations.delete(element);
  element.classList.remove('is-landing-entry-active');
  animation.cancel();

  if (activeAnimations.size === 0 && root.dataset.landingEntryState === 'running') {
    completeWithoutMotion();
  }
};

const completeWithoutMotion = () => {
  if (entryComplete) {
    return;
  }

  entryComplete = true;
  window.clearTimeout(entryWatchdog);
  const shouldNotify = root.dataset.landingEntryState !== 'complete' ||
    root.hasAttribute('data-landing-entry-arrival');
  root.dataset.landingEntryState = 'complete';
  root.removeAttribute('data-landing-entry-arrival');

  activeAnimations.forEach((animation, element) => {
    element.classList.remove('is-landing-entry-active');
    animation.cancel();
  });
  activeAnimations.clear();
  removeInterruptionListeners();
  if (shouldNotify) {
    document.dispatchEvent(new CustomEvent('sigma:landing-entry-complete'));
  }
};

const isInitiallyVisible = (bounds, viewportHeight, headerBottom) => {
  return (
    bounds.width > 0 &&
    bounds.height > 0 &&
    bounds.right > 0 &&
    bounds.left < window.innerWidth &&
    bounds.bottom > headerBottom &&
    bounds.top < viewportHeight
  );
};

const startLandingEntry = () => {
  if (root.dataset.landingEntryStarted === 'true') {
    return;
  }

  if (
    !landing ||
    root.dataset.landingEntryState !== 'pending' ||
    reducedMotion.matches ||
    document.hidden ||
    typeof Element.prototype.animate !== 'function'
  ) {
    completeWithoutMotion();
    return;
  }

  // Read every viewport and element measurement before adding classes or animations.
  const viewportHeight = window.innerHeight;
  const header = document.querySelector('.site-header');
  const headerBottom = header instanceof HTMLElement
    ? Math.max(0, header.getBoundingClientRect().bottom)
    : 0;
  const candidates = Array.from(landing.querySelectorAll(ENTRY_SELECTOR));
  const measurements = candidates.map((element) => ({
    bounds: element.getBoundingClientRect(),
    element,
  }));
  const isDesktopLayout = window.matchMedia('(min-width: 1024px)').matches;
  const transitions = isDesktopLayout ? DESKTOP_TRANSITIONS : TRANSITIONS;
  const wordStagger = isDesktopLayout ? 24 : 40;
  const visualStagger = isDesktopLayout ? 110 : 80;
  const typeIndexes = new Map();
  const typeCounts = new Map();

  candidates.forEach((element) => {
    const type = element.dataset.landingEntry;
    const count = typeCounts.get(type) || 0;
    typeIndexes.set(element, count);
    typeCounts.set(type, count + 1);
  });

  const animationPlans = measurements.flatMap(({ bounds, element }) => {
    const transition = transitions[element.dataset.landingEntry];

    if (
      !transition ||
      !isInitiallyVisible(bounds, viewportHeight, headerBottom)
    ) {
      return [];
    }

    const typeIndex = typeIndexes.get(element) || 0;
    const wordIndex = transition === transitions.word ? typeIndex : 0;
    const visualIndex = transition === transitions.visual ? typeIndex : 0;
    const visualContentIndex =
      transition === transitions['visual-content'] ? typeIndex : 0;
    const delay =
      transition.delay +
      Math.max(0, wordIndex) * wordStagger +
      Math.max(0, visualIndex, visualContentIndex) * visualStagger;

    return [{ delay, element, transition }];
  });

  if (animationPlans.length === 0) {
    completeWithoutMotion();
    return;
  }

  const timelineStart = document.timeline.currentTime;
  root.dataset.landingEntryStarted = 'true';

  animationPlans.forEach(({ delay, element, transition }) => {
    element.classList.add('is-landing-entry-active');

    let animation;

    try {
      animation = element.animate(transition.keyframes, {
        delay,
        duration: transition.duration,
        easing: ENTRY_EASING,
        fill: 'both',
      });

      if (timelineStart !== null) {
        animation.startTime = timelineStart;
      }

      activeAnimations.set(element, animation);
      animation.finished.then(
        () => cleanupAnimation(element, animation),
        () => cleanupAnimation(element, animation),
      );
    } catch {
      animation?.cancel();
      activeAnimations.delete(element);
      element.classList.remove('is-landing-entry-active');
    }
  });

  if (activeAnimations.size === 0) {
    completeWithoutMotion();
    return;
  }

  root.dataset.landingEntryState = 'running';
  // Keep a stalled browser animation from retaining the entry/privacy gate.
  const sequenceDuration = Math.max(...animationPlans.map(({ delay, transition }) =>
    delay + transition.duration,
  ));
  entryWatchdog = window.setTimeout(completeWithoutMotion, sequenceDuration + 500);
};

function handleEntryFocus(event) {
  if (event.target instanceof Element) {
    completeWithoutMotion();
  }
}

function handleMotionPreferenceChange(event) {
  if (event.matches) {
    completeWithoutMotion();
  }
}

function handleEntryInterruption() {
  completeWithoutMotion();
}

function handleEntryVisibility() {
  if (document.hidden) completeWithoutMotion();
}

window.addEventListener('pagehide', handleEntryInterruption);
document.addEventListener('visibilitychange', handleEntryVisibility);
landing?.addEventListener('focusin', handleEntryFocus);
if (typeof reducedMotion.addEventListener === 'function') {
  reducedMotion.addEventListener('change', handleMotionPreferenceChange);
} else {
  reducedMotion.addListener?.(handleMotionPreferenceChange);
}

window.requestAnimationFrame(startLandingEntry);
